/**
 * Admin list-table chrome. Visual values match the Users Role Management
 * table so later screens can share one look without importing that screen.
 * Consumers: Users → Groups tab, Preferences → Approvals member grid
 * (see adminTableMemberGridStyles + AdminTableNameEmail* components).
 * RoleManagement.tsx is intentionally not a consumer yet.
 */
import { StyleSheet } from 'react-native';

import { colors } from '../../theme/colors';
import { layout, spacing } from '../../theme/spacing';

export const ADMIN_TABLE_PAGE_SIZES = [10, 25, 50] as const;
export type AdminTablePageSize = (typeof ADMIN_TABLE_PAGE_SIZES)[number];

export const ADMIN_TABLE_MIN_PAGE_SIZE = ADMIN_TABLE_PAGE_SIZES[0];

export const adminTableColors = {
  headerBackground: colors.backgroundSectionHeader,
  headerBorder: '#dee2e6',
  headerText: '#495057',
  bodyText: '#212529',
  bodyMuted: '#6c757d',
  rowRule: colors.borderCard,
  pagerText: '#666666',
  buttonBorder: colors.borderMedium,
  buttonText: '#333333',
  buttonTextDisabled: '#999999',
} as const;

export const adminTableStyles = StyleSheet.create({
  frame: {
    width: '100%',
    minWidth: 0,
  },
  table: {
    width: '100%',
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: 8,
    backgroundColor: colors.backgroundPrimary,
  },
  header: {
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
    backgroundColor: adminTableColors.headerBackground,
    borderBottomWidth: 2,
    borderBottomColor: adminTableColors.headerBorder,
    paddingHorizontal: layout.users.tableSectionHorizontalPadding,
  },
  headerText: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    color: adminTableColors.headerText,
    fontSize: 13,
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: adminTableColors.rowRule,
    paddingHorizontal: layout.users.tableSectionHorizontalPadding,
  },
  primaryCell: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    color: adminTableColors.bodyText,
    fontSize: 13,
    fontWeight: '500',
  },
  secondaryCell: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    color: adminTableColors.bodyMuted,
    fontSize: 11,
  },
  pager: {
    width: '100%',
    minWidth: 0,
    marginTop: spacing.lg,
    paddingTop: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.borderMedium,
    gap: spacing.sm,
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  /**
   * Range text and the rows-per-page control share the first line so the
   * page buttons get a full line to themselves and never wrap off-screen.
   */
  pagerSummary: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    minWidth: 0,
    maxWidth: '100%',
  },
  pagerRange: {
    flexShrink: 1,
    minWidth: 0,
    color: adminTableColors.pagerText,
    fontSize: 14,
  },
  pagerSize: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '100%',
  },
  pagerControls: {
    width: '100%',
    alignSelf: 'stretch',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    maxWidth: '100%',
  },
  pagerLabel: {
    fontSize: 14,
    color: adminTableColors.pagerText,
  },
  pageSizeButton: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: adminTableColors.buttonBorder,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    minWidth: 50,
    minHeight: 36,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageSizeText: {
    fontSize: 14,
    color: adminTableColors.buttonText,
    fontWeight: '500',
  },
  pageButton: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: adminTableColors.buttonBorder,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    minHeight: 36,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageButtonDisabled: {
    opacity: 0.5,
  },
  pageButtonText: {
    fontSize: 14,
    color: adminTableColors.buttonText,
    fontWeight: '500',
  },
  pageButtonTextDisabled: {
    color: adminTableColors.buttonTextDisabled,
  },
  pageInfo: {
    fontSize: 14,
    color: adminTableColors.pagerText,
  },
});
